import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import Landing from './pages/Landing';
import AdminWaitlist from './pages/AdminWaitlist';
import Auth from './pages/Auth';
import Feed from './pages/Feed';
import Discovery from './pages/Discovery';
import DetailedProfile from './pages/DetailedProfile';
import Messages from './pages/Messages';
import Bookings from './pages/Bookings';
import Notifications from './pages/Notifications';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import SavedVideos from './pages/SavedVideos';
import PaymentMethod from './pages/PaymentMethod';
import ReviewsPage from './pages/ReviewsPage';
import InstallPrompt from './components/InstallPrompt';
import AdminDashboard from './pages/AdminDashboard';
import Lobby from './pages/Lobby';
import ResetPassword from './pages/ResetPassword';
import Layout from './components/Layout';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { syncPush, enablePush, pushSupported } from './lib/push';

// Keeps this device's push subscription linked to the signed-in user; offers to turn push on once.
function PushSync() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user?.id || !pushSupported()) return;
    if (Notification.permission === 'granted') { syncPush(); return; }
    if (Notification.permission !== 'default' || sessionStorage.getItem('push-asked')) return;
    sessionStorage.setItem('push-asked', '1');
    const t = setTimeout(() => toast('Get alerts for messages and sessions?', {
      description: 'Turn on notifications so you never miss a booking or call.',
      duration: 15000,
      action: { label: 'Turn on', onClick: () => enablePush().then(r => r.ok && toast.success('Notifications on')) },
    }), 4000);
    return () => clearTimeout(t);
  }, [user?.id]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen bg-black flex items-center justify-center"><div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" /></div>;
  // When ads point at `/`, an un-authed visitor hits this branch. Redirect to
  // `/auth` (a route App.js treats as a full-page, scrollable landing) rather
  // than rendering <Auth /> in place — the latter inherits `.App.app-mode`
  // + `body.no-scroll` and locks scrolling on every mobile browser.
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  const isFullPage = ['/landing', '/auth', '/reset-password'].includes(location.pathname) || location.pathname.startsWith('/admin') || location.pathname.startsWith('/lobby');

  useEffect(() => {
    if (isFullPage) {
      document.body.classList.remove('no-scroll');
    } else {
      document.body.classList.add('no-scroll');
    }
    return () => document.body.classList.remove('no-scroll');
  }, [isFullPage]);

  return (
    <div className={`App ${isFullPage ? 'landing-mode' : 'app-mode'}`}>
      <Routes>
        <Route path="/landing" element={<Landing />} />
        <Route path="/admin/waitlist" element={<AdminWaitlist />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/lobby/:bookingId" element={<ProtectedRoute><Lobby /></ProtectedRoute>} />
        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<Feed />} />
          <Route path="discovery" element={<Discovery />} />
          <Route path="discovery/:id" element={<DetailedProfile />} />
          <Route path="messages" element={<Messages />} />
          <Route path="bookings" element={<Bookings />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="profile" element={<Profile />} />
          <Route path="settings" element={<Settings />} />
          <Route path="settings/saved" element={<SavedVideos />} />
          <Route path="settings/payment" element={<PaymentMethod />} />
          <Route path="settings/reviews" element={<ReviewsPage />} />
          <Route path="admin" element={<AdminDashboard />} />
        </Route>
      </Routes>
    </div>
  );
}

function AuthPage() {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return <Auth />;
}

function App() {
  useEffect(() => {
    // Register the service worker once on app boot so push notifications work
    // when the tab is closed. Silent no-op on browsers without support.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => { /* silently ignore in dev */ });
    }
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <PushSync />
        <InstallPrompt />
        <Toaster position="top-center" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
